import { Fragment, ReactNode, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { IEventSection, IEventTab } from '@features/club/typings-meta';
import { ClubEventListWidget } from '@features/club/ui/ClubEventList';
import { ITab } from '@features/club/ui/ClubEventList/constants';
import { buildSectionFilters, getSectionTabs } from '@features/club/utils/event-sections';

type Props = {
  className?: string;
  clubId: string;
  sections: IEventSection[];
  // текущие search-параметры страницы клуба
  search: Record<string, unknown>;
  // виджеты, которые рендерятся после раздела с данным id
  slots?: Record<string, ReactNode>;
};

type OnTabChange = (section: IEventSection, tabId: string, subId?: string) => void;

export function ClubEventSectionsWidget({ className, clubId, sections, search, slots }: Props) {
  const navigate = useNavigate({ from: '/club/$clubId' });
  const { isManager } = useClubRoles(clubId);

  const onTabChange = useCallback<OnTabChange>(
    (section, tabId, subId) => {
      const tabs = getSectionTabs(section);
      const tab = tabs.find((t) => t.id === tabId);

      // первая вкладка и первая подвкладка — значения по умолчанию, в URL не пишем
      navigate({
        search: (prev) => {
          const next: Record<string, unknown> = {
            ...prev,
            [section.id]: tabId === tabs[0]?.id ? undefined : tabId,
          };
          // сбрасываем подвкладки всех вкладок раздела, потом ставим активную
          for (const t of tabs) next[subKey(section, t)] = undefined;
          if (tab && subId && subId !== tab.subTabs?.[0]?.id) next[subKey(section, tab)] = subId;
          return next;
        },
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <>
      {sections.map((section) => (
        <Fragment key={section.id}>
          <Section
            className={className}
            clubId={clubId}
            section={section}
            search={search}
            isManager={isManager}
            onTabChange={onTabChange}
          />
          {slots?.[section.id]}
        </Fragment>
      ))}
    </>
  );
}

function Section({
  className,
  clubId,
  section,
  search,
  isManager,
  onTabChange,
}: {
  className?: string;
  clubId: string;
  section: IEventSection;
  search: Record<string, unknown>;
  isManager: boolean;
  onTabChange: OnTabChange;
}) {
  const tabs = getSectionTabs(section);
  const tab = resolveTab(tabs, search[section.id], isManager);
  const sub = tab.subTabs && resolveTab(tab.subTabs, search[subKey(section, tab)], isManager);

  const { customFilter, customQuery } = useMemo(() => buildSectionFilters(section), [section]);
  const customTabs = useMemo(() => toTabs(tabs), [tabs]);
  const customSubTabs = useMemo(() => tab.subTabs && toTabs(tab.subTabs), [tab]);

  const onTab = useCallback(
    (tabId: string, subId?: string) => onTabChange(section, tabId, subId),
    [onTabChange, section],
  );

  return (
    <ClubEventListWidget
      id={section.id}
      className={className}
      clubId={clubId}
      tab={tab.id}
      subTab={sub?.id}
      hideNews={!section.news}
      eventsTheme={section.theme}
      customTitle={section.title}
      customTabs={customTabs}
      customSubTabs={customSubTabs}
      customFilter={customFilter}
      customQuery={customQuery}
      customEventEntranceText={section.entranceText}
      onTabChange={onTab}
    />
  );
}

// Неизвестное значение из URL и черновики для не-менеджера — первая вкладка
function resolveTab(tabs: IEventTab[], value: unknown, isManager: boolean): IEventTab {
  const tab = typeof value === 'string' ? tabs.find((t) => t.id === value) : undefined;
  return tab && (tab.id !== 'drafts' || isManager) ? tab : tabs[0]!;
}

function subKey(section: IEventSection, tab: IEventTab) {
  return tab.subParam ?? `${section.id}Sub`;
}

function toTabs(tabs: IEventTab[]): ITab[] {
  return tabs.map((t) => ({ value: t.id, label: t.label }));
}
